import { autonomyOf } from "../autonomy"
import { deriveId } from "../hash"
import { isWorkingMember } from "../multiplayer"
import { versionsOf } from "../outputs"
import { capabilityVerdict } from "../policy"
import { openProposal } from "../runs"
import { agentOf, artifactOf, isOpenItem, isRunEnded, itemOf, leadOf, must, runOf } from "../state"
import type { Actor, Command, Ids, WorkRequestDraft } from "../types"
import { workDocumentRefOf } from "../work-artifacts"
import { servesProjectWork } from "../work-scope"
import { cancelCascade } from "./graph"
import { actorTaint, forbidden, invalid, type Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>
const requests = (tx: Tx) => tx.state.workRequests ?? []

const withdrawSupersededDrafts = (tx: Tx, requestId: Ids["WorkRequestId"]): void => {
  for (const proposal of tx.state.proposals)
    if (
      (proposal.state === "open" || proposal.state === "revision_requested") &&
      proposal.body._tag === "project_request" &&
      proposal.body.draft.id === requestId
    )
      tx.emit({ _tag: "ProposalWithdrawn", proposalId: proposal.id })
}

/** Authorizing requester and actual resource principal remain separate. */
const requester = (tx: Tx): Ids["UserId"] => {
  if (tx.actor._tag === "user") {
    if (!isWorkingMember(tx.state, tx.actor.userId))
      forbidden("not_owner", "the requester is no longer a working member")
    return tx.actor.userId
  }
  const actor = tx.actor
  if (actor._tag !== "agent") forbidden("actor_kind", "persistent agents request project work")
  const run = runOf(tx.state, actor.runId)
  if (run?.kind !== "turn" || run.agentId !== actor.agentId || isRunEnded(run))
    forbidden("actor_kind", "request project work from your own live turn")
  const who = run.askedBy?._tag === "user" ? run.askedBy.userId : run.ownerId
  if (!isWorkingMember(tx.state, who)) forbidden("not_owner", "the requester is no longer a working member")
  return who
}

export const applyProjectWork = (
  tx: Tx,
  draft: WorkRequestDraft,
  author: Actor = tx.actor,
  tainted = actorTaint(tx.state, author) === "tainted",
): void => {
  const project = must(tx.state.project, "project")
  if (draft.target.projectId === project.id)
    invalid("use this project's ordinary plan and ask tools", "target.projectId")
  if (draft.itemId !== null && !itemOf(tx.state, draft.itemId))
    invalid("no such source work item", "draft.itemId")
  const existing = requests(tx).find((r) => r.id === draft.id)
  if (draft.expectedRevision !== undefined && draft.expectedRevision !== existing?.revision)
    invalid("responsibility changed; read its current revision before editing", "draft.expectedRevision")
  if (existing && existing.sourceProjectId !== project.id) invalid("revise an outgoing request", "draft.id")
  if (
    existing &&
    tx.actor._tag === "agent" &&
    tx.actor.agentId !== existing.sourceAgentId &&
    tx.actor.agentId !== leadOf(tx.state)?.id
  )
    forbidden("not_owner", "only the requesting agent or project lead revises this request")
  const sourceAgentId =
    existing?.sourceAgentId ?? (author._tag === "agent" ? author.agentId : leadOf(tx.state)?.id)
  if (!sourceAgentId) invalid("this project has no lead to receive the result", "draft")
  const who = requester(tx)
  const askedBy = existing?.askedBy ??
    (tx.actor._tag === "agent" ? runOf(tx.state, tx.actor.runId)?.askedBy : undefined) ?? {
      _tag: "user" as const,
      userId: who,
    }
  const knowledge = draft.knowledge.map((d) => {
    const artifact = artifactOf(tx.state, d.artifactId)
    if (!artifact?.doc || !artifact.sha256)
      invalid("knowledge must be a stored document in the source project", "draft.knowledge")
    return must(workDocumentRefOf(tx.state, artifact), "source document")
  })
  const title = draft.title.trim()
  const body = draft.body.trim()
  if (!title || !body) invalid("a request needs its goal and work description", "draft")
  withdrawSupersededDrafts(tx, draft.id)
  tx.emit({
    _tag: "ProjectWorkRequested",
    request: {
      ...draft,
      title,
      body,
      knowledge,
      sourceProjectId: project.id,
      sourceProjectName: project.name,
      sourceAgentId,
      createdBy: existing?.createdBy ?? author,
      authorizedBy: tx.actor,
      requester: who,
      askedBy,
      revision: (existing?.revision ?? 0) + 1,
      createdAt: existing?.createdAt ?? tx.now,
      updatedAt: tx.now,
      tainted: tainted || knowledge.some((d) => d.tainted),
      state: "pending",
      replies: existing?.replies ?? [],
      retired: existing
        ? [
            ...(existing.retired ?? []),
            ...(existing.state === "cancelled"
              ? []
              : [
                  {
                    revision: existing.revision,
                    target: existing.target,
                    requester: who,
                    authorizedBy: tx.actor,
                    note: "Responsibility revised or retargeted",
                    at: tx.now,
                    delivery: { _tag: "pending" as const },
                  },
                ]),
          ]
        : [],
      delivery: { _tag: "pending" },
    },
  })
}

export const requestProjectWork = (tx: Tx, c: Cmd<"RequestProjectWork">): void => {
  requester(tx)
  const actor = tx.actor
  if (actor._tag === "agent") {
    const agent = must(agentOf(tx.state, actor.agentId), "agent")
    const verdict = capabilityVerdict(tx.state, agent, "coordinate_projects")
    if (verdict === "never") forbidden("policy_never", "coordinate_projects is forbidden by this policy")
    const level = autonomyOf(tx.state, actor)
    if (level === "suggest") forbidden("autonomy", "suggest the project request in chat")
    if (verdict === "propose" || level === "ask") {
      const who = requester(tx)
      openProposal(tx, {
        id: c.proposalId,
        ownerId: who,
        body: { _tag: "project_request", draft: c.draft },
        proposedBy: actor,
        reasoning: "Request scoped work from another project's persistent agent.",
        oneLine: `${c.draft.target.projectName}: ${c.draft.title}`,
        risk: "low",
        reversibility: "reversible",
        tier: "soon",
        blocks: [],
        tainted: actorTaint(tx.state, actor) === "tainted",
        approver: { _tag: "human", userId: who },
        decideBy: null,
        expiresAt: null,
        intentKey: `project-request:${c.draft.id}`,
      })
      return
    }
  }
  applyProjectWork(tx, c.draft)
}

export const receiveProjectWork = (tx: Tx, c: Cmd<"ReceiveProjectWork">): void => {
  const project = must(tx.state.project, "project")
  if (c.request.target.projectId !== project.id || c.request.sourceProjectId === project.id)
    invalid("wrong project for this request", "request")
  const agent = agentOf(tx.state, c.request.target.agentId)
  if (!agent || agent.ownerId !== c.request.target.executionPrincipal)
    invalid("the assigned agent or principal changed", "request.target")
  const old = requests(tx).find((r) => r.id === c.request.id)
  if (old && old.sourceProjectId !== c.request.sourceProjectId)
    invalid("responsibility id belongs to another source project", "request.id")
  if (old && old.revision >= c.request.revision) return
  if (old) stopAssociatedWork(tx, old.id, old.revision, "Responsibility revised or retargeted")
  tx.emit({
    _tag: "ProjectWorkReceived",
    request: { ...c.request, replies: old?.replies ?? [], state: "pending", delivery: { _tag: "delivered" } },
  })
}

const mayControl = (tx: Tx, r: import("../types").WorkRequest): void => {
  requester(tx)
  if (tx.actor._tag !== "agent") return
  if (tx.actor.agentId !== r.sourceAgentId && tx.actor.agentId !== leadOf(tx.state)?.id)
    forbidden("not_owner", "only the requesting agent or project lead controls this responsibility")
  if (
    capabilityVerdict(tx.state, must(agentOf(tx.state, tx.actor.agentId), "agent"), "coordinate_projects") ===
    "never"
  )
    forbidden("policy_never", "coordinate_projects is forbidden")
}

export const cancelProjectWork = (tx: Tx, c: Cmd<"CancelProjectWork">): void => {
  const r = requests(tx).find((r) => r.id === c.requestId)
  if (!r || r.sourceProjectId !== tx.state.project?.id)
    invalid("cancel an outgoing responsibility", "requestId")
  mayControl(tx, r)
  if (r.revision !== c.revision)
    invalid("read the current request revision before controlling it", "revision")
  if (r.state === "cancelled") return
  if (!c.note.trim()) invalid("say why the work stops", "note")
  withdrawSupersededDrafts(tx, r.id)
  tx.emit({
    _tag: "ProjectWorkCancelled",
    requestId: r.id,
    revision: r.revision,
    note: c.note.trim(),
    requester: requester(tx),
  })
}

export const linkProjectWork = (tx: Tx, c: Cmd<"LinkProjectWork">): void => {
  const r = requests(tx).find((r) => r.id === c.requestId)
  const item = itemOf(tx.state, c.itemId)
  if (!r || r.target.projectId !== tx.state.project?.id || r.state !== "pending" || r.revision !== c.revision)
    invalid("link to a current pending incoming responsibility", "requestId")
  if (!item || !isOpenItem(item)) invalid("link an open work item", "itemId")
  requester(tx)
  if (
    tx.actor._tag === "agent" &&
    tx.actor.agentId !== r.target.agentId &&
    tx.actor.agentId !== leadOf(tx.state)?.id
  )
    forbidden("not_owner", "the assigned agent or project lead links work")
  if (
    tx.actor._tag === "agent" &&
    capabilityVerdict(tx.state, must(agentOf(tx.state, tx.actor.agentId), "agent"), "coordinate_projects") ===
      "never"
  )
    forbidden("policy_never", "coordinate_projects is forbidden")
  if (item.workRequest) {
    if (item.workRequest.requestId === r.id && item.workRequest.revision === c.revision) return
    invalid("this item already belongs to another responsibility revision", "itemId")
  }
  tx.emit({ _tag: "ProjectWorkLinked", requestId: r.id, revision: c.revision, itemId: item.id })
}

const stopAssociatedWork = (
  tx: Tx,
  requestId: Ids["WorkRequestId"],
  revision: number,
  note: string,
): void => {
  for (const item of tx.state.items)
    if (
      item.workRequest?.requestId === requestId &&
      item.workRequest.revision <= revision &&
      isOpenItem(item)
    )
      cancelCascade(tx, item, note)
  for (const run of tx.state.runs) {
    if (run.kind !== "turn" || isRunEnded(run) || run.cancelRequestedAt !== undefined) continue
    // Only an exclusively scoped turn is safe to interrupt; mixed turns receive the cancellation wake.
    if (run.workScope?.exclusive && servesProjectWork(run, requestId, revision))
      tx.emit({ _tag: "RunCancelRequested", runId: run.id, reason: note, requestedAt: tx.now })
  }
}

export const receiveWorkControl = (tx: Tx, c: Cmd<"ReceiveWorkControl">): void => {
  const r = requests(tx).find((r) => r.id === c.requestId)
  if (!r || r.target.projectId !== tx.state.project?.id) return
  stopAssociatedWork(tx, r.id, c.revision, c.note)
  if (r.revision > c.revision || r.state === "cancelled") return
  tx.emit({
    _tag: "ProjectWorkStopped",
    requestId: r.id,
    revision: c.revision,
    note: c.note,
    ...(c.assignment ? { assignment: c.assignment } : {}),
  })
  const items = tx.state.items.filter(
    (item) => item.workRequest?.requestId === r.id && item.workRequest.revision <= c.revision,
  )
  const ids = new Set(items.map((item) => item.id))
  const artifacts = tx.state.artifacts.filter((a) => a.itemId !== null && ids.has(a.itemId))
  const live = tx.state.runs.filter(
    (run) =>
      !isRunEnded(run) &&
      ((run.itemId !== null && ids.has(run.itemId)) ||
        (run.kind === "turn" && servesProjectWork(run, r.id, c.revision))),
  )
  tx.emit({
    _tag: "ProjectWorkReplied",
    requestId: r.id,
    reply: {
      id: deriveId("cmd", tx.now, `work-control:${r.id}:${c.revision}`),
      revision: c.revision,
      outcome: "partial",
      summary: `Stop requested: ${c.note}. ${items.length ? `Associated items: ${items.map((i) => `${i.ref} (${i.status})`).join(", ")}. ` : ""}${artifacts.length ? `Preserved outputs: ${artifacts.map((a) => `${a.ref ?? a.id}: ${a.title}`).join(", ")}. ` : ""}${live.length ? `Awaiting actual stop reports from runs: ${live.map((run) => run.id).join(", ")}.` : "No associated live runs remain."}`,
      links: artifacts.flatMap((a) => (a.uri.match(/^https?:\/\//) ? [a.uri] : [])).slice(0, 20),
      by: tx.actor,
      at: tx.now,
      tainted: r.tainted,
      delivery: { _tag: "pending" },
    },
  })
}

export const replyProjectWork = (tx: Tx, c: Cmd<"ReplyProjectWork">): void => {
  const r = requests(tx).find((r) => r.id === c.requestId)
  if (!r || r.target.projectId !== tx.state.project?.id) invalid("reply to an incoming request", "requestId")
  const actor = tx.actor
  if (actor._tag === "agent") {
    const run = runOf(tx.state, actor.runId)
    if (
      run?.kind !== "turn" ||
      run.agentId !== actor.agentId ||
      isRunEnded(run) ||
      run.ownerId !== r.target.executionPrincipal ||
      (actor.agentId !== r.target.agentId && actor.agentId !== leadOf(tx.state)?.id)
    )
      forbidden("not_owner", "the assigned agent and principal reply from their own turn")
    if (
      capabilityVerdict(tx.state, must(agentOf(tx.state, actor.agentId), "agent"), "coordinate_projects") ===
      "never"
    )
      forbidden("policy_never", "coordinate_projects is forbidden")
  } else if (actor._tag !== "user") forbidden("actor_kind", "agents or people reply to project work")
  if (c.reply.revision > r.revision) invalid("this request revision does not exist", "reply.revision")
  const artifacts = c.reply.artifacts?.map((returned) => {
    const artifact = artifactOf(tx.state, returned.artifact.id)
    if (
      returned.projectId !== tx.state.project?.id ||
      !artifact ||
      versionsOf(tx.state, artifact).length !== returned.revision
    )
      invalid("return an exact stored artifact version from this project", "reply.artifacts")
    const document = workDocumentRefOf(tx.state, artifact)
    const { document: _claimedDocument, ...reference } = returned
    return {
      ...reference,
      artifact,
      url: !artifact.doc && /^https?:\/\//.test(artifact.uri) ? artifact.uri : returned.url,
      ...(document ? { document } : {}),
    }
  })
  if (r.replies.some((x) => x.id === c.reply.id)) return
  tx.emit({
    _tag: "ProjectWorkReplied",
    requestId: r.id,
    reply: {
      ...c.reply,
      ...(artifacts ? { artifacts } : {}),
      by: actor,
      at: tx.now,
      tainted:
        actorTaint(tx.state, actor) === "tainted" || (artifacts?.some((a) => a.artifact.tainted) ?? false),
      delivery: { _tag: "pending" },
    },
  })
}

export const receiveProjectReply = (tx: Tx, c: Cmd<"ReceiveProjectReply">): void => {
  const r = requests(tx).find((r) => r.id === c.requestId)
  if (!r || r.sourceProjectId !== tx.state.project?.id)
    invalid("no outgoing request for this result", "requestId")
  if (r.replies.some((x) => x.id === c.reply.id)) return
  tx.emit({ _tag: "ProjectWorkReplyReceived", requestId: r.id, reply: c.reply })
}

export const recordWorkDelivery = (tx: Tx, c: Cmd<"RecordWorkDelivery">): void => {
  const r = requests(tx).find((r) => r.id === c.requestId)
  if (
    !r ||
    (c.control
      ? !r.retired?.some((a) => a.revision === c.revision)
      : c.replyId
        ? !r.replies.some((reply) => reply.id === c.replyId)
        : r.revision !== c.revision)
  )
    return
  tx.emit({
    _tag: "WorkDeliveryRecorded",
    requestId: r.id,
    revision: c.revision,
    problem: c.problem,
    ...(c.replyId ? { replyId: c.replyId } : {}),
    ...(c.control ? { control: true } : {}),
  })
}

/** Requesters assess the specific returned version; accepting it never closes the source item. */
export const verifyProjectWork = (tx: Tx, c: Cmd<"VerifyProjectWork">): void => {
  const r = requests(tx).find((r) => r.id === c.requestId)
  if (!r || r.sourceProjectId !== tx.state.project?.id) invalid("verify an outgoing request", "requestId")
  const actor = tx.actor
  if (actor._tag === "agent") {
    const run = runOf(tx.state, actor.runId)
    if (
      run?.kind !== "turn" ||
      isRunEnded(run) ||
      run.agentId !== actor.agentId ||
      (actor.agentId !== r.sourceAgentId && actor.agentId !== leadOf(tx.state)?.id)
    )
      forbidden("not_owner", "the requesting agent or lead verifies returned results")
  } else if (actor._tag !== "user") forbidden("actor_kind", "agents or people verify results")
  const reply = r.replies.find((x) => x.id === c.verification.replyId)
  if (!reply?.artifacts?.some((x) => x.artifact.id === c.verification.artifactId))
    invalid("choose an artifact returned in this reply", "verification.artifactId")
  if (r.verifications?.some((v) => v.id === c.verification.id)) return
  tx.emit({
    _tag: "ProjectWorkVerified",
    requestId: r.id,
    verification: { ...c.verification, by: actor, at: tx.now },
  })
}
